import { ChevronLeft, Truck, Zap, Check, Package, MapPin, Clock } from "lucide-react";
import { Button } from "@/app/components/button";
import { CheckoutProgress } from "@/app/components/checkout-progress";
import { formatCurrency } from "@/config/locale";
import { LOCAL_HANDOFF_SHIPPING_OPTION_ID } from "@/app/api/auth-client";

export interface ShippingOption {
  id: string;
  name: string;
  description: string;
  estimatedDays: string;
  price: number;
  type: "standard" | "express";
  /** When hub routing (swap): amount the buyer pays (Leg 1.1 + Leg 2). */
  buyerAmount?: number;
  /** When hub routing (swap): amount the seller pays (Leg 1 + Leg 2.1). Not shown in UI. */
  sellerAmount?: number;
  /** Hub: Leg 1 (seller → hub), in reais. Sent to create order for persistence. */
  leg1?: number;
  /** This party's cost for leg Origin → Hub (Leg 1.1), in reais. Shown above route. */
  leg1_1?: number;
  /** This party's cost for leg Hub → Destination (Leg 2), in reais. Shown above route. */
  leg2?: number;
  /** Hub: Leg 2.1 (hub → seller), in reais. Sent to create order for persistence. */
  leg2_1?: number;
  /** Estimated days for leg Origin → Hub (Leg 1.1). Shown under route connector. */
  leg1_1EstimatedDays?: number;
  /** Estimated days for leg Hub → Destination (Leg 2). Shown under route connector. */
  leg2EstimatedDays?: number;
  /** When hub routing: estimated days for Leg 1 (seller → hub). Used for seller view. */
  leg1EstimatedDays?: number;
  /** When hub routing: estimated days for Leg 2.1 (hub → seller). Used for seller view. */
  leg2_1EstimatedDays?: number;
  /** P2 audit (cents). One-way: carrier raw + markup. Hub: also use buyer/seller party fields below. */
  rawPriceCents?: number;
  freightMarkupCents?: number;
  buyerRawPriceCents?: number;
  buyerFreightMarkupCents?: number;
  sellerRawPriceCents?: number;
  sellerFreightMarkupCents?: number;
}

/** Route labels for hub visualization (origin → hub → destination). */
export interface ShippingRouteLabels {
  origin: string;
  hub: string;
  destination: string;
}

interface CheckoutShippingPageProps {
  onBack: () => void;
  shippingOptions: ShippingOption[];
  shippingOptionsLoading?: boolean;
  shippingOptionsError?: string | null;
  selectedShippingId: string | null;
  onSelectShipping: (shippingId: string) => void;
  onContinue: () => void;
  /** When present (hub/swap), show route visualization above options. */
  routeLabels?: ShippingRouteLabels | null;
  /** Shown under shippingOptionsError (e.g. second-party checkout after a failed quote). */
  onRetryShippingQuote?: () => void;
  /** When set (e.g. second-party checkout), replaces default "Opções de Envio" title. Subtitle omitted if `shippingPageSubtitle` not passed. */
  shippingPageTitle?: string;
  shippingPageSubtitle?: string;
  /** "order" = copy aligned to pagamento da 2ª parte (pedido); default = checkout carrinho. */
  shippingInfoBanner?: "checkout" | "order";
}

/** Coerce to string to avoid rendering objects as React children (error #300). */
function safeText(x: unknown): string {
  if (x == null) return "";
  if (typeof x === "string" || typeof x === "number") return String(x);
  return "";
}

/**
 * Clean carrier description for display:
 * - Removes "(4 pernas)".
 * - Removes per-leg ETA ("Entrega em até N dias úteis") so we don't show two conflicting ETAs;
 *   the only ETA shown is the total (estimatedDays) below.
 */
function descriptionForDisplay(description: string): string {
  let s = String(description ?? "").replace(/\s*\(4\s*pernas\)\s*/gi, " ").trim();
  s = s.replace(/\s*—\s*Entrega em até \d+\s*dia(s)? úteis?\s*/gi, " — ").trim();
  s = s.replace(/\s*Entrega em até \d+\s*dia(s)? úteis?\s*—?\s*/gi, " ").trim();
  return s.replace(/\s*—\s*—/g, " —").trim();
}

export function CheckoutShippingPage({
  onBack,
  shippingOptions,
  shippingOptionsLoading = false,
  shippingOptionsError = null,
  selectedShippingId,
  onSelectShipping,
  onContinue,
  routeLabels,
  onRetryShippingQuote,
  shippingPageTitle,
  shippingPageSubtitle,
  shippingInfoBanner = "checkout",
}: CheckoutShippingPageProps) {
  const getShippingIcon = (option: ShippingOption) => {
    if (option.id === LOCAL_HANDOFF_SHIPPING_OPTION_ID) {
      return <MapPin className="w-6 h-6 text-primary" />;
    }
    switch (option.type) {
      case "standard":
        return <Truck className="w-6 h-6 text-primary" />;
      case "express":
        return <Zap className="w-6 h-6 text-primary" />;
      default:
        return <Package className="w-6 h-6 text-primary" />;
    }
  };

  return (
    <div className="min-h-screen bg-background pb-8">
      {/* Header */}
      <header className="bg-primary border-b border-primary sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>
            <div>
              <h1 className="text-xl text-white">{shippingPageTitle ?? "Opções de Envio"}</h1>
              {shippingPageTitle == null ? (
                <p className="text-sm text-white/80">Escolha como deseja receber</p>
              ) : shippingPageSubtitle ? (
                <p className="text-sm text-white/80">{shippingPageSubtitle}</p>
              ) : null}
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Progress Indicator */}
        <CheckoutProgress currentStep={2} stepLabel="Envio" totalSteps={3} />

        {/* Info Card */}
        <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900 rounded-lg p-4 mb-6">
          <div className="flex gap-3">
            <Package className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-blue-900 dark:text-blue-100 font-medium mb-1">
                Selecione a melhor opção para você
              </p>
              <p className="text-xs text-blue-700 dark:text-blue-300">
                {shippingInfoBanner === "order"
                  ? "As opções de envio usam os endereços e dados do pedido (troca via hub), no mesmo critério da etapa do comprador."
                  : "As opções de envio são calculadas com base no seu endereço e nos produtos do carrinho."}
              </p>
            </div>
          </div>
        </div>

        {/* Route visualization (hub/swap): origin → hub → destination, with quote amount and estimated days per leg */}
        {routeLabels && (
          <div className="mb-6 rounded-lg border border-border bg-muted/30 dark:bg-muted/10 p-4">
            <p className="text-xs font-medium text-muted-foreground mb-3">Trajeto do envio</p>
            {(() => {
              const selectedOption = selectedShippingId
                ? shippingOptions.find((o) => o.id === selectedShippingId)
                : shippingOptions[0];
              const legToHub = selectedOption?.leg1_1;
              const legToDest = selectedOption?.leg2;
              const daysToHub = selectedOption?.leg1_1EstimatedDays;
              const daysToDest = selectedOption?.leg2EstimatedDays;
              const showAmounts = legToHub != null || legToDest != null;
              const formatDays = (d: number) => (d === 1 ? "1 dia útil" : `${d} dias úteis`);
              return (
                <div className="flex items-center gap-2 sm:gap-4">
                  <div className="flex flex-col items-center min-w-0 flex-1">
                    <MapPin className="w-5 h-5 text-primary flex-shrink-0 mb-1" aria-hidden />
                    <span className="text-xs font-medium text-foreground truncate w-full text-center" title={safeText(routeLabels.origin)}>
                      {safeText(routeLabels.origin)}
                    </span>
                    <span className="text-[10px] text-muted-foreground">Origem</span>
                  </div>
                  <div className="flex flex-col items-center flex-shrink-0 w-8 sm:w-12">
                    {showAmounts && (
                      <span className="text-[10px] font-medium text-primary mb-1">
                        {legToHub != null ? formatCurrency(legToHub) : "—"}
                      </span>
                    )}
                    <div className="w-8 h-0.5 bg-border rounded sm:w-12" aria-hidden />
                    {daysToHub != null && daysToHub > 0 && (
                      <span className="text-[10px] text-muted-foreground mt-1 text-center">
                        {formatDays(daysToHub)}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-col items-center min-w-0 flex-1">
                    <MapPin className="w-5 h-5 text-primary flex-shrink-0 mb-1" aria-hidden />
                    <span className="text-xs font-medium text-foreground truncate w-full text-center" title={safeText(routeLabels.hub)}>
                      {safeText(routeLabels.hub)}
                    </span>
                    <span className="text-[10px] text-muted-foreground">Seu Hub Mais Próximo</span>
                  </div>
                  <div className="flex flex-col items-center flex-shrink-0 w-8 sm:w-12">
                    {showAmounts && (
                      <span className="text-[10px] font-medium text-primary mb-1">
                        {legToDest != null ? formatCurrency(legToDest) : "—"}
                      </span>
                    )}
                    <div className="w-8 h-0.5 bg-border rounded sm:w-12" aria-hidden />
                    {daysToDest != null && daysToDest > 0 && (
                      <span className="text-[10px] text-muted-foreground mt-1 text-center">
                        {formatDays(daysToDest)}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-col items-center min-w-0 flex-1">
                    <MapPin className="w-5 h-5 text-primary flex-shrink-0 mb-1" aria-hidden />
                    <span className="text-xs font-medium text-foreground truncate w-full text-center" title={safeText(routeLabels.destination)}>
                      {safeText(routeLabels.destination)}
                    </span>
                    <span className="text-[10px] text-muted-foreground">Destino</span>
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* Loading / empty / list */}
        {shippingOptionsLoading && (
          <div className="flex items-center justify-center py-12 text-muted-foreground">
            <p>Carregando opções de envio…</p>
          </div>
        )}
        {!shippingOptionsLoading && shippingOptions.length === 0 && (
          <div className="rounded-lg border border-border bg-muted/30 p-6 text-center text-muted-foreground mb-6">
            {shippingOptionsError ? (
              <>
                <p>{safeText(shippingOptionsError)}</p>
                {onRetryShippingQuote && (
                  <Button type="button" variant="outline" className="mt-4" onClick={onRetryShippingQuote}>
                    Tentar novamente
                  </Button>
                )}
              </>
            ) : (
              <>
                <p>Nenhuma opção de envio disponível para este endereço e carrinho.</p>
                <p className="text-sm mt-2">Verifique o CEP e os itens do carrinho.</p>
              </>
            )}
          </div>
        )}
        {/* Shipping Options List */}
        <div className="space-y-4 mb-6">
          {!shippingOptionsLoading && shippingOptions.map((option) => (
            <button
              key={option.id}
              onClick={() => onSelectShipping(option.id)}
              className={`w-full p-4 border-2 rounded-lg text-left transition-all ${
                selectedShippingId === option.id
                  ? "border-primary bg-primary/5 shadow-sm"
                  : "border-border hover:border-primary/50"
              }`}
            >
              <div className="flex items-start gap-4">
                {/* Selection Radio */}
                <div
                  className={`mt-1 w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                    selectedShippingId === option.id
                      ? "border-primary bg-primary"
                      : "border-border"
                  }`}
                >
                  {selectedShippingId === option.id && (
                    <Check className="w-3 h-3 text-white" />
                  )}
                </div>

                {/* Icon */}
                <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                  {getShippingIcon(option)}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <h3 className="font-semibold text-base">{safeText(option.name)}</h3>
                    <span className="text-lg font-bold text-primary flex-shrink-0">
                      {Number(option.price) === 0 ? "Grátis" : formatCurrency(Number(option.price) || 0)}
                    </span>
                  </div>
                  {option.buyerAmount != null && (
                    <p className="text-sm text-muted-foreground mb-1">
                      Sua parte do frete: <strong className="text-foreground">{Number(option.buyerAmount) === 0 ? "Grátis" : formatCurrency(Number(option.buyerAmount) || 0)}</strong>
                    </p>
                  )}
                  {(option.leg1_1 != null || option.leg2 != null) && (
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground mb-2">
                      {option.leg1_1 != null && (
                        <span>
                          Trecho 1: {formatCurrency(option.leg1_1)}
                          {option.leg1_1EstimatedDays != null && option.leg1_1EstimatedDays > 0 && (
                            <> — {option.leg1_1EstimatedDays === 1 ? "1 dia útil" : `${option.leg1_1EstimatedDays} dias úteis`}</>
                          )}
                        </span>
                      )}
                      {option.leg2 != null && (
                        <span>
                          Trecho 2: {formatCurrency(option.leg2)}
                          {option.leg2EstimatedDays != null && option.leg2EstimatedDays > 0 && (
                            <> — {option.leg2EstimatedDays === 1 ? "1 dia útil" : `${option.leg2EstimatedDays} dias úteis`}</>
                          )}
                        </span>
                      )}
                    </div>
                  )}
                    <p className="text-sm text-muted-foreground mb-2">
                    {descriptionForDisplay(safeText(option.description))}
                  </p>
                  <div className="flex items-center gap-2">
                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-secondary rounded-full">
                      <Clock className="w-3.5 h-3.5 text-muted-foreground" aria-hidden />
                      <span className="text-xs font-medium text-muted-foreground">
                        Prazo total: {safeText(option.estimatedDays)}
                      </span>
                    </div>
                    {option.type === "express" && (
                      <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-yellow-100 dark:bg-yellow-900/30 rounded-full">
                        <Zap className="w-3.5 h-3.5 text-yellow-600 dark:text-yellow-400" />
                        <span className="text-xs font-medium text-yellow-700 dark:text-yellow-300">
                          Entrega Expressa
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Selected Indicator */}
              {selectedShippingId === option.id && (
                <div className="mt-3 pt-3 border-t border-primary/20">
                  <div className="flex items-center gap-2 text-sm text-primary">
                    <Check className="w-4 h-4" />
                    <span className="font-medium">Opção selecionada</span>
                  </div>
                </div>
              )}
            </button>
          ))}
        </div>

        {/* Continue Button */}
        <Button
          onClick={onContinue}
          disabled={!selectedShippingId || shippingOptionsLoading}
          className="w-full"
          size="lg"
        >
          Continuar para Revisão
        </Button>

        {/* Help Text */}
        {!selectedShippingId && !shippingOptionsLoading && (
          <p className="text-xs text-center text-muted-foreground mt-4">
            Selecione uma opção de envio para continuar
          </p>
        )}
      </div>
    </div>
  );
}
